import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { SectionPanel } from '~/components/ui/SectionPanel';
import { statusBadgeClasses } from '~/components/ui/statusBadgeClasses';
import { sellerLabel } from '~/utils';
import type { QuoteSeller } from './splitAwardGridTypes';

function ArchivedQuoteRow({ quote }: Readonly<{ quote: QuoteSeller }>) {
  const { t } = useTranslation('rfq-view');

  return (
    <li className="flex items-center gap-3 rounded-lg border border-neut-border bg-neut-bg-alt p-4">
      <h3 className="text-sm font-semibold text-neut-prim">
        {sellerLabel(quote.sellingOrganization)}
      </h3>
      <span className={statusBadgeClasses('neutral')}>
        {t('rfq_view.quote_status.ARCHIVED')}
      </span>
    </li>
  );
}

export function ArchivedQuoteList({
  quotes,
}: Readonly<{ quotes: ReadonlyArray<QuoteSeller> }>) {
  const { t } = useTranslation('rfq-view');
  const [isExpanded, setIsExpanded] = useState(false);
  const listId = useId();

  return (
    <SectionPanel
      bodyClassName={isExpanded ? 'p-4' : 'hidden'}
      title={
        <button
          type="button"
          aria-expanded={isExpanded}
          aria-controls={isExpanded ? listId : undefined}
          className="flex items-center gap-1.5"
          onClick={() => setIsExpanded((current) => !current)}
        >
          {t('rfq_view.archived_quotes_heading', { count: quotes.length })}
          <ChevronDown
            size={14}
            aria-hidden="true"
            className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>
      }
    >
      {isExpanded && (
        <ul
          id={listId}
          data-testid="archived-quote-list"
          className="flex flex-col gap-3"
        >
          {quotes.map((quote) => (
            <ArchivedQuoteRow key={quote.id} quote={quote} />
          ))}
        </ul>
      )}
    </SectionPanel>
  );
}
